import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Award, Clock, Heart, CheckCircle, Users } from 'lucide-react';
import './About.css';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Honesty First',
      description: 'We believe in transparent communication and fair pricing. No surprises, no hidden fees—just honest advice about what your vehicle needs.'
    },
    {
      icon: Award,
      title: 'Quality Work',
      description: 'Every repair is performed with precision and care. We take pride in our workmanship and stand behind the quality of our service.'
    },
    {
      icon: Heart,
      title: 'Customer Care',
      description: 'You\'re not just another customer—you\'re part of our community. We treat every vehicle as if it were our own.'
    },
    {
      icon: Clock,
      title: 'Respect Your Time',
      description: 'We understand how important your vehicle is to your daily life. We work efficiently to get you back on the road as quickly as possible.'
    }
  ];

  const stats = [
    { number: '2015', label: 'Established' },
    { number: '1000+', label: 'Happy Customers' },
    { number: '10+', label: 'Years Experience' },
    { number: '5★', label: 'Customer Rating' }
  ];

  return (
    <div className="about-page">
      <Header />
      
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-background">
          <img 
            src="https://images.unsplash.com/photo-1596986952526-3be237187071" 
            alt="Moua's Auto Repair"
          />
          <div className="about-hero-overlay"></div>
        </div>
        <div className="container">
          <div className="about-hero-content">
            <h1 className="fade-in-up">About Moua's Auto Repair</h1>
            <p className="about-hero-subtitle fade-in-up">
              Your trusted neighborhood auto repair shop in St. Paul since 2015
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="story-grid">
            <div className="story-image">
              <img 
                src="https://images.unsplash.com/photo-1770656505713-b0fd2f5751e6" 
                alt="Professional auto repair service"
              />
            </div>
            <div className="story-content">
              <h2>Our Story</h2>
              <p>
                Moua's Auto Repair was founded with a simple mission: to provide St. Paul drivers with honest, reliable auto repair service at fair prices. Since 2015, we've built our reputation on integrity, quality workmanship, and treating every customer with respect.
              </p>
              <p>
                Our owner brings years of automotive expertise and a genuine passion for helping people keep their vehicles running safely and efficiently. What started as a small neighborhood shop has grown through word-of-mouth recommendations from satisfied customers who appreciate our straightforward approach to auto repair.
              </p>
              <p>
                We're not a high-pressure sales operation or a faceless corporate chain. We're your neighbors—a local business that understands the importance of trust, reliability, and honest service. When you bring your vehicle to Moua's, you can count on getting the truth about what needs to be fixed, what can wait, and what it will cost.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header text-center">
            <h2>What We Stand For</h2>
            <p className="section-subtitle">
              The principles that guide everything we do
            </p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card card">
                <div className="value-icon-wrapper">
                  <value.icon size={40} className="value-icon" />
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="different-content">
            <h2 className="text-center mb-6">What Makes Us Different</h2>
            <div className="different-grid">
              <div className="different-item">
                <CheckCircle size={28} className="different-icon" />
                <div>
                  <h3>Honest Assessments</h3>
                  <p>We'll never try to sell you services you don't need. If something can wait, we'll tell you. If there's a more affordable solution, we'll suggest it.</p>
                </div>
              </div>
              <div className="different-item">
                <CheckCircle size={28} className="different-icon" />
                <div>
                  <h3>Fair Pricing</h3>
                  <p>Our prices are reasonable and competitive. We provide clear estimates upfront and never surprise you with unexpected charges.</p>
                </div>
              </div>
              <div className="different-item">
                <CheckCircle size={28} className="different-icon" />
                <div>
                  <h3>Friendly Service</h3>
                  <p>Our staff is approachable, professional, and genuinely interested in helping you. We take time to answer questions and explain repairs in plain language.</p>
                </div>
              </div>
              <div className="different-item">
                <CheckCircle size={28} className="different-icon" />
                <div>
                  <h3>Quick Turnaround</h3>
                  <p>We know you rely on your vehicle. Whenever possible, we offer same-day service to minimize disruption to your schedule.</p>
                </div>
              </div>
              <div className="different-item">
                <CheckCircle size={28} className="different-icon" />
                <div>
                  <h3>Local & Accessible</h3>
                  <p>We're part of the St. Paul community. We're easy to reach, conveniently located, and committed to serving our neighbors.</p>
                </div>
              </div>
              <div className="different-item">
                <CheckCircle size={28} className="different-icon" />
                <div>
                  <h3>Experienced Team</h3>
                  <p>Our technicians have the skills and experience to diagnose and repair a wide range of automotive issues correctly the first time.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="section section-light">
        <div className="container">
          <div className="community-content text-center">
            <Users size={48} className="community-icon" />
            <h2>Part of the St. Paul Community</h2>
            <p className="section-subtitle">
              We're proud to serve drivers throughout St. Paul and the surrounding east metro areas. Our success is built on the trust and loyalty of customers who return to us year after year and recommend us to their friends and family.
            </p>
            <p className="section-subtitle">
              When you choose Moua's Auto Repair, you're supporting a local business that cares about this community and the people who live here.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
